import { inject, Injectable } from "@angular/core";
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from "@angular/common/http";
import { Observable, throwError, timer } from "rxjs";
import { catchError, finalize, retry } from "rxjs/operators";

import { LgNetworkErrorService } from "../error-tracking/lg-network-error.service";

/** Delays in ms between successive retry attempts (index 0 = before 2nd attempt, etc.). */
const RETRY_DELAYS_MS = [1000, 3000] as const;

/** RFC 7231 idempotent methods — safe to retry on network-level failure. */
const IDEMPOTENT_METHODS = new Set(["GET", "HEAD", "OPTIONS", "PUT", "DELETE"]);

/**
 * HTTP interceptor that handles status === 0 network failures with retry-before-escalate.
 *
 * For idempotent requests (GET, HEAD, OPTIONS, PUT, DELETE):
 *   - Silently retries up to 2 additional times (delays: 1 000 ms, then 3 000 ms).
 *   - If the network recovers on any retry, the caller receives the response transparently
 *     (no reload, no dialog).
 *   - Each intermediate failure is logged silently via LgNetworkErrorService.
 *   - On final failure (all 3 attempts exhausted), hands off to LgNetworkErrorService
 *     for the reload / dialog escalation path.
 *
 * For non-idempotent requests (POST, PATCH):
 *   - No retry, no auto-reload. Logs once silently and rethrows so the caller's normal
 *     error handling runs.
 *
 * Non-zero-status errors (5xx, timeouts, etc.) pass through without retry or tracking.
 *
 * Interceptor order: This should be placed early in the interceptor chain (close to the caller)
 * to catch errors from auth interceptors and other network infrastructure components.
 */
@Injectable()
export class LgNetworkErrorInterceptor implements HttpInterceptor {
    private _networkErrorService = inject(LgNetworkErrorService);

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        let retryStarted = false;

        return next.handle(req).pipe(
            retry({
                count: RETRY_DELAYS_MS.length,
                delay: (err: unknown, retryCount: number) => {
                    const error = err as HttpErrorResponse;
                    if (!this._shouldTrackError(error) || !this._isIdempotentMethod(req.method)) {
                        return throwError(() => error);
                    }
                    this._networkErrorService.trackError(
                        this._buildErrorMessage(error),
                        { url: error.url ?? req.url, status: error.status },
                        { silent: true }
                    );
                    if (!retryStarted) {
                        retryStarted = true;
                        this._networkErrorService.notifyRetryStarted();
                    }
                    const delayMs =
                        RETRY_DELAYS_MS[retryCount - 1] ??
                        RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
                    return timer(delayMs);
                }
            }),
            catchError((error: HttpErrorResponse) => {
                if (this._shouldTrackError(error)) {
                    const errorMessage = this._buildErrorMessage(error);
                    const context = { url: error.url ?? req.url, status: error.status };
                    if (this._isIdempotentMethod(req.method)) {
                        this._networkErrorService.trackError(errorMessage, context);
                    } else {
                        this._networkErrorService.trackError(errorMessage, context, {
                            silent: true
                        });
                    }
                }
                return throwError(() => error);
            }),
            // finalize fires on success, error, AND unsubscription — covers the cancellation
            // path that tap({complete}) and catchError both miss.
            // Placement AFTER catchError guarantees that on the error path, catchError's
            // synchronous trackError (_cancelPostReloadGracePeriod → _gracePending=false)
            // runs before notifyRetryCompleted checks _gracePending.
            finalize(() => {
                if (retryStarted) {
                    this._networkErrorService.notifyRetryCompleted();
                }
            })
        );
    }

    /**
     * Determine if this error should be tracked.
     * Only track network failures (status 0), not application-level server errors (5xx).
     */
    private _shouldTrackError(error: HttpErrorResponse): boolean {
        return error.status === 0;
    }

    private _isIdempotentMethod(method: string): boolean {
        return IDEMPOTENT_METHODS.has(method.toUpperCase());
    }

    /**
     * Build a descriptive error message for logging
     */
    private _buildErrorMessage(error: HttpErrorResponse): string {
        return `Network error: ${error.message || "Unable to connect to server"}`;
    }
}
